import { useCallback, useRef, useState } from "react";
import { AnimatePresence, useReducedMotion } from "motion/react";
import { useRequiredQuinn } from "../../QuinnContext.tsx";
import { QUINN_MAX_WIDTH, QUINN_MIN_WIDTH } from "../../utils";
import { QuinnCopy } from "../../types";
import { useQuinnPanelHotkeys, useQuinnPanelResize } from "../../hooks";
import { QuinnThread } from "../Thread";
import { QuinnComposer } from "../Composer";
import { QuinnPanelBackdrop } from "./QuinnPanelBackdrop.tsx";
import { QuinnPanelHeader } from "./QuinnPanelHeader.tsx";
import { QuinnPanelResizeHandle } from "./QuinnPanelResizeHandle.tsx";
import { QuinnPanelShell } from "./QuinnPanelShell.tsx";

export function QuinnPanel() {
    const quinn = useRequiredQuinn();
    const prefersReducedMotion = useReducedMotion();
    const [draft, setDraft] = useState("");
    const panelRef = useRef<HTMLDivElement | null>(null);

    const { close, isOpen, isOverlay, setWidth } = quinn;
    const { isResizing, startResize } = useQuinnPanelResize(setWidth);

    useQuinnPanelHotkeys({
        open: quinn.open,
        close,
        startNewChat: quinn.startNewChat,
        isOpen,
        isOverlay,
    });

    const activeChat = quinn.chats.find((chat) => chat.id === quinn.chatId);
    const title = activeChat?.title ?? (quinn.messages.length > 0 ? QuinnCopy.CurrentChat : QuinnCopy.NewChat);

    const handleRetry = useCallback(
        (message: { id: string }) => {
            quinn.retry(message.id);
        },
        [quinn],
    );

    return (
        <AnimatePresence>
            {isOpen && (
                <>
                    {isOverlay && <QuinnPanelBackdrop onClose={close} />}

                    <QuinnPanelShell panelRef={panelRef} width={quinn.width} isResizing={isResizing}>
                        <QuinnPanelResizeHandle isResizing={isResizing} onPointerDown={startResize} />

                        <QuinnPanelHeader
                            chats={quinn.chats}
                            activeChatId={quinn.chatId}
                            title={title}
                            isBusy={quinn.isBusy}
                            prefersReducedMotion={prefersReducedMotion}
                            onOpenChat={quinn.openChat}
                            onNewChat={quinn.startNewChat}
                            onClose={close}
                        />

                        <QuinnThread
                            messages={quinn.messages}
                            isBusy={quinn.isBusy}
                            isHydrating={quinn.isHydrating}
                            pageLabel={quinn.pageContext.label}
                            suggestions={quinn.suggestions}
                            onPickSuggestion={(suggestion) => quinn.send(suggestion)}
                            onRetry={handleRetry}
                            onAnswerPrompt={quinn.answerPrompt}
                            onRate={quinn.rateAnswer}
                        />

                        <QuinnComposer
                            canAskAllClients={quinn.canAskAllClients}
                            scope={quinn.scope}
                            businesses={quinn.businesses}
                            isBusy={quinn.isBusy}
                            isHydrating={quinn.isHydrating}
                            draft={draft}
                            onDraftChange={setDraft}
                            onScopeChange={quinn.setScope}
                            onSubmit={quinn.send}
                            onStop={quinn.stop}
                        />
                    </QuinnPanelShell>
                </>
            )}
        </AnimatePresence>
    );
}
